import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { getShipments, getClients, getCatalog, getNotionStatus, importFromNotion, getImporters, getContainers, exportShipments, deleteShipment, changeShipmentMbl, bulkUpdateShipments, exportShipmentsTemplate, importShipmentsExcel, calculateAllEtas, revalidateShipment, extractPedimentoOnly, repairTrackingNow } from '../lib/api';
import { useAuth } from '../contexts/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { AdvancedFiltersPanel } from '../components/AdvancedFiltersPanel';
import { DateFiltersPanel } from '../components/DateFiltersPanel';
import { BulkEditDialog } from '../components/BulkEditDialog';
import { ExcelImportDialog } from '../components/ExcelImportDialog';
import { ShipmentsTable, ShipmentCard } from '../components/ShipmentsTable';
import { ShipmentsToolbar } from '../components/ShipmentsToolbar';
import { DeleteShipmentDialog } from '../components/DeleteShipmentDialog';
import { EditMblDialog } from '../components/EditMblDialog';
import { BulkPedimentoDialog } from '../components/BulkPedimentoDialog';
import { BulkProformaDialog } from '../components/BulkProformaDialog';
import { useI18n } from '../contexts/I18nContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { toast } from 'sonner';
import { Plus, Search, Filter, RefreshCw, Calendar, Loader2 } from 'lucide-react';

// Hook to detect mobile
function useIsMobile() {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);
  
  return isMobile;
}

export default function ShipmentsPage() {
  const { t } = useI18n();
  const { effectiveRole, isClient, clientViewMode, isAdmin, canManage, hasField } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();
  const isMobile = useIsMobile();
  
  const [shipments, setShipments] = useState([]);
  const [clients, setClients] = useState([]);
  const [importers, setImporters] = useState([]);
  const [statuses, setStatuses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [notionStatus, setNotionStatus] = useState(null);
  const [importing, setImporting] = useState(false);
  const [showDateFilters, setShowDateFilters] = useState(false);
  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [expandedRows, setExpandedRows] = useState({});
  const [deleteDialog, setDeleteDialog] = useState({ open: false, mbl: null, clientName: null });
  const [deleting, setDeleting] = useState(false);
  const [editMblDialog, setEditMblDialog] = useState({ open: false, mbl: null, newMbl: '' });
  const [editingMbl, setEditingMbl] = useState(false);
  const [selectedShipments, setSelectedShipments] = useState([]);
  const [bulkEditDialog, setBulkEditDialog] = useState({ open: false });
  const [bulkPedimentoDialog, setBulkPedimentoDialog] = useState(false);
  const [bulkProformaDialog, setBulkProformaDialog] = useState(false);
  const [bulkEditData, setBulkEditData] = useState({ status_general_code: '', client_id: '', importer_id: '' });
  const [bulkUpdating, setBulkUpdating] = useState(false);
  const [bulkRevalidating, setBulkRevalidating] = useState(false);
  const [prepedUploading, setPrepedUploading] = useState(false);
  const prepedFileRef = useRef(null);
  const [excelImportDialog, setExcelImportDialog] = useState({ open: false });
  const [importingExcel, setImportingExcel] = useState(false);
  const [exportingExcel, setExportingExcel] = useState(false);
  const [calculatingEtas, setCalculatingEtas] = useState(false);
  const fileInputRef = useRef(null);
  
  // Sort state
  const [sortConfig, setSortConfig] = useState({ key: 'default', direction: 'desc' });
  
  const FILTERS_STORAGE_KEY = 'ofimex_shipments_filters';
  
  // Load saved filters from localStorage on mount
  const getSavedFilters = () => {
    try {
      const saved = localStorage.getItem(FILTERS_STORAGE_KEY);
      if (saved) {
        return JSON.parse(saved);
      }
    } catch (e) {
      console.error('Error loading saved filters:', e);
    }
    return null;
  };

  const [filters, setFilters] = useState(() => {
    const saved = getSavedFilters();
    return {
      search: searchParams.get('search') || saved?.search || '',
      client_id: searchParams.get('client_id') || saved?.client_id || 'all',
      importer_id: searchParams.get('importer_id') || saved?.importer_id || 'all',
      status: searchParams.get('status') || saved?.status || 'all',
      // delivery_status replaces the old is_active dropdown + not_delivered
      // checkbox combo. The user only cares about "delivered yes/no"; the
      // is_active soft-flag is no longer surfaced in the UI.
      //   'operating' → status != ENTREGADO  (default; the daily working list)
      //   'delivered' → status == ENTREGADO
      //   'all'       → no delivery filter
      delivery_status: searchParams.get('delivery_status') || saved?.delivery_status || 'operating',
      container_number: searchParams.get('container') || saved?.container_number || '',
      tipo_carga: saved?.tipo_carga || '',
      referencia_carga: saved?.referencia_carga || '',
      numero_pedimento: saved?.numero_pedimento || '',
      eta_arribo_from: saved?.eta_arribo_from || '',
      eta_arribo_to: saved?.eta_arribo_to || '',
      entrega_from: saved?.entrega_from || '',
      entrega_to: saved?.entrega_to || '',
      // Advanced filters (added)
      carrier_code: saved?.carrier_code || 'all',
      terminal_code: saved?.terminal_code || 'all',
      agente_aduanal: saved?.agente_aduanal || '',
      semaforo: saved?.semaforo || 'all',
      has_pedimento: saved?.has_pedimento ?? 'all',  // 'all' | 'yes' | 'no'
      min_days_in_port: saved?.min_days_in_port || '',
    };
  });

  // Save filters to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(FILTERS_STORAGE_KEY, JSON.stringify(filters));
    } catch (e) {
      console.error('Error saving filters:', e);
    }
  }, [filters]);

  useEffect(() => {
    loadData();
    checkNotionStatus();
  }, []);

  const checkNotionStatus = async () => {
    try {
      const res = await getNotionStatus();
      setNotionStatus(res.data);
    } catch (err) {
      console.log('Notion not configured');
    }
  };

  // Convert the UI's `delivery_status` value into the backend's
  // `not_delivered` query param. `operating` → only non-delivered shipments,
  // `delivered` → only delivered, `all` → no filter (undefined).
  const deliveryStatusToParam = (v) => {
    if (v === 'operating') return true;
    if (v === 'delivered') return false;
    return undefined;
  };

  useEffect(() => {
    const params = new URLSearchParams();
    if (filters.search) params.set('search', filters.search);
    if (filters.client_id && filters.client_id !== 'all') params.set('client_id', filters.client_id);
    if (filters.importer_id && filters.importer_id !== 'all') params.set('importer_id', filters.importer_id);
    if (filters.status && filters.status !== 'all') params.set('status', filters.status);
    if (filters.delivery_status && filters.delivery_status !== 'all') params.set('delivery_status', filters.delivery_status);
    if (filters.container_number) params.set('container', filters.container_number);
    setSearchParams(params);
  }, [filters, setSearchParams]);

  // Auto-search with debounce for text inputs (search, container_number, tipo_carga, referencia, pedimento)
  useEffect(() => {
    const debounceTimer = setTimeout(() => {
      if (filters.search !== undefined || filters.container_number !== undefined || 
          filters.tipo_carga !== undefined || filters.referencia_carga !== undefined ||
          filters.numero_pedimento !== undefined) {
        loadDataWithFilters(filters);
      }
    }, 400); // 400ms debounce

    return () => clearTimeout(debounceTimer);
  }, [filters.search, filters.container_number, filters.tipo_carga, filters.referencia_carga, filters.numero_pedimento]);

  const loadData = async () => {
    setLoading(true);
    try {
      const apiFilters = {
        client_id: filters.client_id === 'all' ? undefined : filters.client_id,
        importer_id: filters.importer_id === 'all' ? undefined : filters.importer_id,
        // Specific status and delivery-state filters now COMBINE (AND) on the
        // backend, so always pass the chosen status alongside not_delivered.
        status: filters.status === 'all' ? undefined : filters.status,
        not_delivered: deliveryStatusToParam(filters.delivery_status),
        container_number: filters.container_number || undefined,
        tipo_carga: filters.tipo_carga || undefined,
        referencia_carga: filters.referencia_carga || undefined,
        numero_pedimento: filters.numero_pedimento || undefined,
        eta_arribo_from: filters.eta_arribo_from || undefined,
        eta_arribo_to: filters.eta_arribo_to || undefined,
        entrega_from: filters.entrega_from || undefined,
        entrega_to: filters.entrega_to || undefined,
        carrier_code: filters.carrier_code && filters.carrier_code !== 'all' ? filters.carrier_code : undefined,
        terminal_code: filters.terminal_code && filters.terminal_code !== 'all' ? filters.terminal_code : undefined,
        agente_aduanal: filters.agente_aduanal || undefined,
        semaforo: filters.semaforo && filters.semaforo !== 'all' ? filters.semaforo : undefined,
        has_pedimento: filters.has_pedimento === 'yes' ? true : filters.has_pedimento === 'no' ? false : undefined,
        min_days_in_port: filters.min_days_in_port ? Number(filters.min_days_in_port) : undefined,
      };
      
      const [shipmentsRes, clientsRes, importersRes, statusesRes] = await Promise.all([
        getShipments(apiFilters),
        !isClient ? getClients() : Promise.resolve({ data: [] }),
        canManage ? getImporters() : Promise.resolve({ data: [] }),
        getCatalog('shipment_status'),
      ]);
      
      setShipments(shipmentsRes.data);
      setClients(clientsRes.data);
      setImporters(importersRes.data);
      setStatuses(statusesRes.data);
      setSelectedShipments([]); // Clear selection on reload
    } catch (err) {
      console.error('Error loading shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  const filteredShipments = shipments.filter(s => {
    if (!filters.search) return true;
    const search = filters.search.trim().toLowerCase();
    if (!search) return true;
    return (
      s.mbl.toLowerCase().includes(search) ||
      s.client_name?.toLowerCase().includes(search) ||
      s.importer_name?.toLowerCase().includes(search) ||
      s.origin?.toLowerCase().includes(search) ||
      s.destination?.toLowerCase().includes(search)
    );
  });

  // Sorted shipments
  const sortedShipments = useMemo(() => {
    const list = [...filteredShipments];
    if (sortConfig.key === 'default') return list;
    
    list.sort((a, b) => {
      let valA, valB;
      switch (sortConfig.key) {
        case 'mbl':
          valA = (a.mbl || '').toLowerCase();
          valB = (b.mbl || '').toLowerCase();
          break;
        case 'client':
          valA = (a.client_name || '').toLowerCase();
          valB = (b.client_name || '').toLowerCase();
          break;
        case 'importer':
          valA = (a.importer_name || '').toLowerCase();
          valB = (b.importer_name || '').toLowerCase();
          break;
        case 'containers':
          valA = a.container_count || 0;
          valB = b.container_count || 0;
          break;
        case 'eta_real':
          valA = a.eta_real || a.eta_initial || '';
          valB = b.eta_real || b.eta_initial || '';
          break;
        case 'eta_initial':
          valA = a.eta_initial || a.eta_port || '';
          valB = b.eta_initial || b.eta_port || '';
          break;
        case 'eta_entrega':
          valA = a.eta_entrega_estimada || a.eta_final || '';
          valB = b.eta_entrega_estimada || b.eta_final || '';
          break;
        case 'updated':
          valA = a.last_update_at || a.updated_at || '';
          valB = b.last_update_at || b.updated_at || '';
          break;
        case 'days':
          valA = a.days_total || 0;
          valB = b.days_total || 0;
          break;
        default:
          return 0;
      }
      if (valA < valB) return sortConfig.direction === 'asc' ? -1 : 1;
      if (valA > valB) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return list;
  }, [filteredShipments, sortConfig]);

  const handleFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  // Auto-load when select filters change (client, importer, status, is_active)
  const handleSelectFilterChange = (key, value) => {
    setFilters(prev => ({ ...prev, [key]: value }));
    // Trigger reload after state update
    setTimeout(() => {
      loadDataWithFilters({ ...filters, [key]: value });
    }, 0);
  };

  // Load data with specific filters (for auto-reload)
  const loadDataWithFilters = async (currentFilters) => {
    setLoading(true);
    try {
      const apiFilters = {
        client_id: currentFilters.client_id === 'all' ? undefined : currentFilters.client_id,
        importer_id: currentFilters.importer_id === 'all' ? undefined : currentFilters.importer_id,
        status: currentFilters.status === 'all' ? undefined : currentFilters.status,
        not_delivered: deliveryStatusToParam(currentFilters.delivery_status),
        container_number: currentFilters.container_number || undefined,
        tipo_carga: currentFilters.tipo_carga || undefined,
        referencia_carga: currentFilters.referencia_carga || undefined,
        numero_pedimento: currentFilters.numero_pedimento || undefined,
        eta_arribo_from: currentFilters.eta_arribo_from || undefined,
        eta_arribo_to: currentFilters.eta_arribo_to || undefined,
        entrega_from: currentFilters.entrega_from || undefined,
        entrega_to: currentFilters.entrega_to || undefined,
        carrier_code: currentFilters.carrier_code && currentFilters.carrier_code !== 'all' ? currentFilters.carrier_code : undefined,
        terminal_code: currentFilters.terminal_code && currentFilters.terminal_code !== 'all' ? currentFilters.terminal_code : undefined,
        agente_aduanal: currentFilters.agente_aduanal || undefined,
        semaforo: currentFilters.semaforo && currentFilters.semaforo !== 'all' ? currentFilters.semaforo : undefined,
        has_pedimento: currentFilters.has_pedimento === 'yes' ? true : currentFilters.has_pedimento === 'no' ? false : undefined,
        min_days_in_port: currentFilters.min_days_in_port ? Number(currentFilters.min_days_in_port) : undefined,
      };
      
      const [shipmentsRes, clientsRes, importersRes, statusesRes] = await Promise.all([
        getShipments(apiFilters),
        !isClient ? getClients() : Promise.resolve({ data: [] }),
        canManage ? getImporters() : Promise.resolve({ data: [] }),
        getCatalog('shipment_status'),
      ]);
      
      setShipments(shipmentsRes.data);
      setClients(clientsRes.data);
      setImporters(importersRes.data);
      setStatuses(statusesRes.data);
      setSelectedShipments([]);
    } catch (err) {
      console.error('Error loading shipments:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadData();
  };

  const handleImportFromNotion = async () => {
    setImporting(true);
    try {
      const res = await importFromNotion(false);
      if (res.data.success) {
        const stats = res.data.stats;
        toast.success(`Importación completada: ${stats.created} creados, ${stats.updated} actualizados, ${stats.skipped} omitidos`);
        loadData();
      } else {
        toast.error(res.data.error || 'Error en la importación');
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar desde Notion');
    } finally {
      setImporting(false);
    }
  };

  const clearDateFilters = () => {
    setFilters(prev => ({
      ...prev,
      eta_arribo_from: '',
      eta_arribo_to: '',
      entrega_from: '',
      entrega_to: '',
    }));
  };

  const toggleRowExpand = (mbl) => {
    setExpandedRows(prev => ({
      ...prev,
      [mbl]: !prev[mbl]
    }));
  };

  const handleExport = async () => {
    try {
      toast.info(t('shipments.exportingExcel'));
      const apiFilters = {
        client_id: filters.client_id === 'all' ? undefined : filters.client_id,
        status: filters.status === 'all' ? undefined : filters.status,
        not_delivered: deliveryStatusToParam(filters.delivery_status),
      };
      const res = await exportShipments(apiFilters);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `embarques_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('shipments.exportedOk'));
    } catch (err) {
      toast.error(t('shipments.exportError'));
      console.error('Export error:', err);
    }
  };

  const handleDeleteClick = (shipment) => {
    setDeleteDialog({ 
      open: true, 
      mbl: shipment.mbl, 
      clientName: shipment.client_name 
    });
  };

  const handleDeleteConfirm = async () => {
    if (!deleteDialog.mbl) return;
    
    setDeleting(true);
    try {
      await deleteShipment(deleteDialog.mbl);
      toast.success(t('shipments.deletedOk', { mbl: deleteDialog.mbl }));
      setDeleteDialog({ open: false, mbl: null, clientName: null });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.deleteError'));
    } finally {
      setDeleting(false);
    }
  };

  const handleEditMblClick = (shipment) => {
    setEditMblDialog({ open: true, mbl: shipment.mbl, newMbl: shipment.mbl });
  };

  const handleRevalidateShipment = async (mbl) => {
    const ok = window.confirm(t('shipments.revalidateShipmentConfirm', { mbl }));
    if (!ok) return;
    const tid = toast.loading(t('shipments.revalidating', { mbl }));
    try {
      const res = await revalidateShipment(mbl);
      const d = res?.data || {};
      toast.success(
        `${d.mbl}: ${d.revalidated_ok ?? 0}/${d.total_containers ?? 0} OK · ${d.deleted_port_records ?? 0} · ${d.deleted_ferrovalle_records ?? 0}`,
        { id: tid, duration: 8000 }
      );
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('shipments.bulkUpdateError'), { id: tid });
    }
  };

  const handleBulkRevalidate = async () => {
    if (selectedShipments.length === 0) return;
    const ok = window.confirm(t('shipments.bulkRevalidateConfirm', { count: selectedShipments.length }));
    if (!ok) return;
    setBulkRevalidating(true);
    const tid = toast.loading(t('shipments.bulkRevalidating', { count: selectedShipments.length }));
    let okCount = 0;
    let failCount = 0;
    let totalPort = 0;
    let totalFer = 0;
    for (const mbl of selectedShipments) {
      try {
        const res = await revalidateShipment(mbl);
        const d = res?.data || {};
        okCount += 1;
        totalPort += d.deleted_port_records ?? 0;
        totalFer += d.deleted_ferrovalle_records ?? 0;
      } catch (err) {
        failCount += 1;
      }
    }
    toast.success(
      t('shipments.bulkRevalidateResult', { ok: okCount, fail: failCount, port: totalPort, fer: totalFer }),
      { id: tid, duration: 10000 }
    );
    setBulkRevalidating(false);
    setSelectedShipments([]);
    loadData();
  };

  const handlePrePedimentoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) {
      toast.error(t('shipments.prePedimentoOnlyPdf'));
      if (prepedFileRef.current) prepedFileRef.current.value = '';
      return;
    }
    setPrepedUploading(true);
    const tid = toast.loading(t('shipments.prePedimentoAnalyzing'));
    try {
      const formData = new FormData();
      formData.append('file', file);
      // No params: el backend auto-detecta MBL/contenedor del PDF
      const res = await extractPedimentoOnly(formData, {});
      const d = res.data || {};
      const refTxt = d.reference ? ` · Ref ${d.reference}` : '';
      toast.success(
        `Pedimento ${d.pedimento_number} → ${d.total_containers_updated} contenedor(es) ${d.mbl}${refTxt}`,
        { id: tid, duration: 9000 }
      );
      loadData();
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('shipments.prePedimentoError'), { id: tid });
    } finally {
      setPrepedUploading(false);
      if (prepedFileRef.current) prepedFileRef.current.value = '';
    }
  };

  const handleRepairNow = async () => {
    if (!window.confirm(t('shipments.repairConfirm'))) return;
    const tid = toast.loading(t('shipments.repairTriggered'));
    try {
      await repairTrackingNow();
      toast.success(t('shipments.repairRunning'), { id: tid, duration: 10000 });
    } catch (err) {
      toast.error(err?.response?.data?.detail || t('shipments.repairError'), { id: tid });
    }
  };

  const handleEditMblConfirm = async () => {
    if (!editMblDialog.newMbl || editMblDialog.newMbl === editMblDialog.mbl) {
      setEditMblDialog({ open: false, mbl: null, newMbl: '' });
      return;
    }
    
    setEditingMbl(true);
    try {
      const res = await changeShipmentMbl(editMblDialog.mbl, editMblDialog.newMbl);
      toast.success(res.data.message);
      setEditMblDialog({ open: false, mbl: null, newMbl: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.mblChangeError'));
    } finally {
      setEditingMbl(false);
    }
  };

  const handleSelectShipment = (mbl, checked) => {
    if (checked) {
      setSelectedShipments(prev => [...prev, mbl]);
    } else {
      setSelectedShipments(prev => prev.filter(m => m !== mbl));
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      setSelectedShipments(sortedShipments.map(s => s.mbl));
    } else {
      setSelectedShipments([]);
    }
  };

  const handleBulkEdit = async () => {
    if (selectedShipments.length === 0) return;
    
    setBulkUpdating(true);
    try {
      const params = {};
      if (bulkEditData.status_general_code) params.status_general_code = bulkEditData.status_general_code;
      if (bulkEditData.client_id) params.client_id = bulkEditData.client_id;
      if (bulkEditData.importer_id) params.importer_id = bulkEditData.importer_id;
      
      const res = await bulkUpdateShipments(selectedShipments, params);
      toast.success(res.data.message);
      setBulkEditDialog({ open: false });
      setBulkEditData({ status_general_code: '', client_id: '', importer_id: '' });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || t('shipments.bulkUpdateError'));
    } finally {
      setBulkUpdating(false);
    }
  };

  // Excel Export - download template for editing
  const handleExportExcel = async (onlySelected = false) => {
    setExportingExcel(true);
    try {
      const params = {
        not_delivered: deliveryStatusToParam(filters.delivery_status),
      };
      
      if (onlySelected && selectedShipments.length > 0) {
        params.selected_mbls = selectedShipments.join(',');
      }
      
      toast.info(t('shipments.generatingTemplate'));
      const res = await exportShipmentsTemplate(params);
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `embarques_edicion_${new Date().toISOString().slice(0,10)}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success(t('shipments.templateDownloaded'));
    } catch (err) {
      toast.error(t('shipments.exportError'));
      console.error('Export error:', err);
    } finally {
      setExportingExcel(false);
    }
  };

  // Excel Import - upload edited file
  const handleImportExcel = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    
    setImportingExcel(true);
    try {
      toast.info(t('common.processingExcel'));
      const res = await importShipmentsExcel(file);
      
      const { updated, skipped, errors } = res.data;
      
      if (errors && errors.length > 0) {
        toast.warning(`${updated} actualizados, ${errors.length} errores`);
        console.warn('Import errors:', errors);
      } else {
        toast.success(res.data.message);
      }
      
      setExcelImportDialog({ open: false });
      loadData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al importar archivo');
    } finally {
      setImportingExcel(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Calculate ETAs - predict delivery dates for pending shipments
  const handleCalculateEtas = async () => {
    setCalculatingEtas(true);
    try {
      const res = await calculateAllEtas();
      const { shipments_updated } = res.data;
      
      if (shipments_updated && shipments_updated.length > 0) {
        toast.success(`ETAs calculados para ${shipments_updated.length} embarques`);
      } else {
        toast.info('No hay embarques pendientes para calcular ETA');
      }
      
      loadData(); // Refresh to show updated ETAs
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Error al calcular ETAs');
    } finally {
      setCalculatingEtas(false);
    }
  };

  // Client view: simplified columns
  const showInternalColumns = effectiveRole !== 'client';
  const isClientView = effectiveRole === 'client';

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="shipments-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-3xl">
              {isClientView ? t('nav.myDashboard') : t('shipments.title')}
            </h1>
            <p className="text-muted-foreground" data-testid="shipments-count">
              {isClientView 
                ? `${filteredShipments.length} ${t('shipments.countSuffix')} ${t('common.found') || ''}`.trim()
                : clientViewMode 
                  ? 'Vista Cliente' 
                  : `${filteredShipments.length} ${t('shipments.countSuffix')}`
              }
            </p>
          </div>
          {!isClientView && !isMobile && (
            <ShipmentsToolbar
              showInternalColumns={showInternalColumns}
              isAdmin={isAdmin}
              selectedShipments={selectedShipments}
              exportingExcel={exportingExcel}
              prepedUploading={prepedUploading}
              prepedFileRef={prepedFileRef}
              bulkRevalidating={bulkRevalidating}
              calculatingEtas={calculatingEtas}
              onExportExcel={handleExportExcel}
              onOpenExcelImport={() => setExcelImportDialog({ open: true })}
              onLegacyExport={handleExport}
              onPrePedimentoUpload={handlePrePedimentoUpload}
              onOpenBulkPedimento={() => setBulkPedimentoDialog(true)}
              onOpenBulkProforma={() => setBulkProformaDialog(true)}
              onRepairNow={handleRepairNow}
              onOpenBulkEdit={() => setBulkEditDialog({ open: true })}
              onBulkRevalidate={handleBulkRevalidate}
              onCalculateEtas={handleCalculateEtas}
            />
          )}
        </div>

        {/* Filter Bar */}
        <div className="bg-card border border-border rounded-sm p-3 md:p-4">
          {/* Mobile: Search + Filter Toggle */}
          {isMobile && (
            <div className="flex gap-2 mb-2">
              <div className="flex-1">
                <Input
                  placeholder={t('shipments.searchMbl')}
                  value={filters.search}
                  onChange={(e) => handleFilterChange('search', e.target.value)}
                  className="bg-background"
                  data-testid="search-input"
                />
              </div>
              <Button variant="outline" size="icon" data-testid="mobile-filter-toggle" onClick={() => setShowDateFilters(!showDateFilters)}>
                <Filter size={16} />
              </Button>
              <Button variant="ghost" size="icon" onClick={loadData}>
                <RefreshCw size={16} />
              </Button>
            </div>
          )}
          
          {/* Mobile: Collapsible Filters */}
          {isMobile && showDateFilters && (
            <div className="space-y-2 pt-2 border-t border-border">
              {showInternalColumns && (
                <>
                  <Select 
                    value={filters.client_id} 
                    onValueChange={(v) => handleSelectFilterChange('client_id', v)}
                  >
                    <SelectTrigger data-testid="filter-client-mobile">
                      <SelectValue placeholder={t('shipments.allClients')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('shipments.allClients')}</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <Select 
                    value={filters.status} 
                    onValueChange={(v) => handleSelectFilterChange('status', v)}
                  >
                    <SelectTrigger data-testid="filter-status-mobile">
                      <SelectValue placeholder={t('shipments.allStatus')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('shipments.allStatus')}</SelectItem>
                      {statuses.map(s => (
                        <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  
                  <div className="w-full">
                    <Label className="text-xs text-muted-foreground">{t('shipments.deliveryStatus')}</Label>
                    <Select
                      value={filters.delivery_status}
                      onValueChange={(v) => handleSelectFilterChange('delivery_status', v)}
                    >
                      <SelectTrigger className="mt-1" data-testid="filter-delivery-status-mobile">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="operating">{t('shipments.inOperation')}</SelectItem>
                        <SelectItem value="delivered">{t('shipments.deliveredOnly')}</SelectItem>
                        <SelectItem value="all">{t('shipments.all')}</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </>
              )}
            </div>
          )}
          
          {/* Desktop Filters */}
          {!isMobile && (
          <div className="flex flex-wrap gap-3 items-end">
            <div className="flex-1 min-w-[200px]">
              <Input
                placeholder={isClientView ? t('common.search') + " MBL..." : t('shipments.searchPlaceholder')}
                value={filters.search}
                onChange={(e) => handleFilterChange('search', e.target.value)}
                className="bg-background"
                data-testid="search-input"
              />
            </div>
            
            {showInternalColumns && (
              <div className="w-36">
                <Input
                  placeholder={t('shipments.contNumberPlaceholder')}
                  value={filters.container_number}
                  onChange={(e) => handleFilterChange('container_number', e.target.value)}
                  className="bg-background"
                  data-testid="container-search"
                />
              </div>
            )}
            
            {showInternalColumns && (
              <div className="w-32">
                <Input
                  placeholder={t('shipments.cargoTypePlaceholder')}
                  value={filters.tipo_carga}
                  onChange={(e) => handleFilterChange('tipo_carga', e.target.value)}
                  className="bg-background"
                  data-testid="tipo-carga-search"
                />
              </div>
            )}
            
            {showInternalColumns && (
              <div className="w-32">
                <Input
                  placeholder={t('shipments.referencePlaceholder')}
                  value={filters.referencia_carga}
                  onChange={(e) => handleFilterChange('referencia_carga', e.target.value)}
                  className="bg-background"
                  data-testid="referencia-search"
                />
              </div>
            )}
            
            {showInternalColumns && (
              <div className="w-32">
                <Input
                  placeholder={t('shipments.pedimentoPlaceholder')}
                  value={filters.numero_pedimento}
                  onChange={(e) => handleFilterChange('numero_pedimento', e.target.value)}
                  className="bg-background"
                  data-testid="pedimento-search"
                />
              </div>
            )}
            
            {showInternalColumns && (
              <>
                <div className="w-44">
                  <Select 
                    value={filters.client_id} 
                    onValueChange={(v) => handleSelectFilterChange('client_id', v)}
                  >
                    <SelectTrigger data-testid="filter-client">
                      <SelectValue placeholder={t('shipments.allClients')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('shipments.allClients')}</SelectItem>
                      {clients.map(c => (
                        <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="w-44">
                  <Select 
                    value={filters.importer_id} 
                    onValueChange={(v) => handleSelectFilterChange('importer_id', v)}
                  >
                    <SelectTrigger data-testid="filter-importer">
                      <SelectValue placeholder={t('shipments.allImporters')} />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">{t('shipments.allImporters')}</SelectItem>
                      {importers.map(i => (
                        <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </>
            )}
            
            <div className="w-44">
              <Select 
                value={filters.status} 
                onValueChange={(v) => handleSelectFilterChange('status', v)}
              >
                <SelectTrigger data-testid="filter-status">
                  <SelectValue placeholder={t('shipments.allStatus')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('shipments.allStatus')}</SelectItem>
                  {statuses.map(s => (
                    <SelectItem key={s.code} value={s.code}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {showInternalColumns && (
              <div className="w-48">
                <Select 
                  value={filters.delivery_status} 
                  onValueChange={(v) => handleSelectFilterChange('delivery_status', v)}
                >
                  <SelectTrigger data-testid="filter-delivery-status">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="operating">{t('shipments.inOperation')}</SelectItem>
                    <SelectItem value="delivered">{t('shipments.deliveredOnly')}</SelectItem>
                    <SelectItem value="all">{t('shipments.all')}</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
            
            {(filters.search || filters.container_number || filters.tipo_carga || filters.referencia_carga || filters.numero_pedimento) && (
              <Button variant="secondary" size="sm" onClick={handleApplyFilters} data-testid="apply-filters">
                <Search size={14} className="mr-1" />
                {t('common.search')}
              </Button>
            )}
            
            {!isClientView && (
              <Button 
                variant="outline" 
                onClick={() => setShowDateFilters(!showDateFilters)}
                data-testid="date-filters-toggle"
              >
                <Calendar size={16} className="mr-2" />
                {t('shipments.dates')}
              </Button>
            )}

            {!isClientView && showInternalColumns && (
              <Button 
                variant={showAdvancedFilters ? "secondary" : "outline"}
                onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
                data-testid="advanced-filters-toggle"
              >
                <Filter size={16} className="mr-2" />
                {t('shipments.advanced')}
                {(
                  (filters.carrier_code && filters.carrier_code !== 'all' ? 1 : 0) +
                  (filters.terminal_code && filters.terminal_code !== 'all' ? 1 : 0) +
                  (filters.agente_aduanal ? 1 : 0) +
                  (filters.semaforo && filters.semaforo !== 'all' ? 1 : 0) +
                  (filters.has_pedimento && filters.has_pedimento !== 'all' ? 1 : 0) +
                  (filters.min_days_in_port ? 1 : 0)
                ) > 0 && (
                  <span className="ml-2 inline-flex items-center justify-center min-w-[18px] h-[18px] px-1 text-[10px] font-semibold rounded-full bg-primary text-primary-foreground">
                    {(filters.carrier_code && filters.carrier_code !== 'all' ? 1 : 0) +
                      (filters.terminal_code && filters.terminal_code !== 'all' ? 1 : 0) +
                      (filters.agente_aduanal ? 1 : 0) +
                      (filters.semaforo && filters.semaforo !== 'all' ? 1 : 0) +
                      (filters.has_pedimento && filters.has_pedimento !== 'all' ? 1 : 0) +
                      (filters.min_days_in_port ? 1 : 0)}
                  </span>
                )}
              </Button>
            )}
            
            <Button variant="ghost" onClick={loadData} data-testid="refresh-btn">
              <RefreshCw size={16} />
            </Button>
          </div>
          )}
          
          {/* Date Filters */}
          {showDateFilters && !isMobile && (
            <DateFiltersPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onClear={clearDateFilters}
            />
          )}

          {/* Advanced Filters */}
          {showAdvancedFilters && !isMobile && showInternalColumns && (
            <AdvancedFiltersPanel
              filters={filters}
              onFilterChange={handleFilterChange}
              onSelectFilterChange={handleSelectFilterChange}
              onClear={() => {
                const cleared = {
                  ...filters,
                  carrier_code: 'all',
                  terminal_code: 'all',
                  agente_aduanal: '',
                  semaforo: 'all',
                  has_pedimento: 'all',
                  min_days_in_port: '',
                };
                setFilters(cleared);
                loadDataWithFilters(cleared);
              }}
            />
          )}
        </div>

        {/* Mobile Cards View */}
        {isMobile ? (
          <div className="space-y-3" data-testid="mobile-shipments-list">
            {loading ? (
              <div className="py-8 text-center text-muted-foreground">{t('shipments.loadingShipments')}</div>
            ) : filteredShipments.length === 0 ? (
              <div className="py-8 text-center text-muted-foreground">
                {isClientView ? t('shipments.noActiveForClient') : t('shipments.noShipmentsFound')}
              </div>
            ) : (
              sortedShipments.map((shipment) => (
                <ShipmentCard
                  key={shipment.mbl}
                  shipment={shipment}
                  showInternalColumns={showInternalColumns}
                  isAdmin={isAdmin}
                  isSelected={selectedShipments.includes(shipment.mbl)}
                  onSelect={handleSelectShipment}
                  onRevalidate={handleRevalidateShipment}
                  onEditMbl={handleEditMblClick}
                  hasField={hasField}
                />
              ))
            )}
          </div>
        ) : (
          <ShipmentsTable
            shipments={sortedShipments}
            loading={loading}
            isClientView={isClientView}
            showInternalColumns={showInternalColumns}
            isAdmin={isAdmin}
            hasField={hasField}
            selectedShipments={selectedShipments}
            expandedRows={expandedRows}
            sortConfig={sortConfig}
            onSort={setSortConfig}
            onSelect={handleSelectShipment}
            onSelectAll={handleSelectAll}
            onToggleExpand={toggleRowExpand}
            onRevalidate={handleRevalidateShipment}
            onEditMbl={handleEditMblClick}
            onDelete={handleDeleteClick}
          />
        )}

        {/* Delete Confirmation Dialog */}
        <DeleteShipmentDialog
          open={deleteDialog.open}
          deleteDialog={deleteDialog}
          deleting={deleting}
          onCancel={() => setDeleteDialog({ open: false, mbl: null, clientName: null })}
          onConfirm={handleDeleteConfirm}
        />

        {/* Edit MBL Dialog */}
        <EditMblDialog
          editMblDialog={editMblDialog}
          editingMbl={editingMbl}
          onChange={(v) => setEditMblDialog({ ...editMblDialog, newMbl: v })}
          onCancel={() => setEditMblDialog({ open: false, mbl: null, newMbl: '' })}
          onConfirm={handleEditMblConfirm}
        />

        {/* Bulk Edit Dialog */}
        <BulkEditDialog
          open={bulkEditDialog.open}
          onOpenChange={(open) => {
            if (!bulkUpdating) {
              setBulkEditDialog({ open });
              if (!open) {
                setBulkEditData({ status_general_code: '', client_id: '', importer_id: '' });
              }
            }
          }}
          selectedCount={selectedShipments.length}
          bulkEditData={bulkEditData}
          setBulkEditData={setBulkEditData}
          statuses={statuses}
          clients={clients}
          importers={importers}
          bulkUpdating={bulkUpdating}
          onConfirm={handleBulkEdit}
        />

        {/* Excel Import Dialog */}
        <ExcelImportDialog
          open={excelImportDialog.open}
          onOpenChange={(open) => setExcelImportDialog({ open })}
          fileInputRef={fileInputRef}
          onFileChange={handleImportExcel}
          importingExcel={importingExcel}
        />

        {/* Bulk Pedimento Upload Dialog */}
        <BulkPedimentoDialog
          open={bulkPedimentoDialog}
          onOpenChange={setBulkPedimentoDialog}
          onSuccess={loadData}
        />

        {/* Bulk Proforma (extract-only) Dialog */}
        <BulkProformaDialog
          open={bulkProformaDialog}
          onOpenChange={setBulkProformaDialog}
          onSuccess={loadData}
        />

        {/* Mobile Floating Action Button */}
        {isMobile && showInternalColumns && (
          <Link to="/shipments/new">
            <Button 
              className="fixed bottom-20 right-4 h-14 w-14 rounded-full shadow-lg z-50"
              size="icon"
              data-testid="mobile-new-shipment-btn"
            >
              <Plus size={24} />
            </Button>
          </Link>
        )}
      </div>
    </DashboardLayout>
  );
}
